import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { soldSlug } from "@/lib/sold-slug";

export interface SoldVehicle {
  slug: string;
  title: string;
  price: number | null;
  category: string;
  soldAt: string;
  daysOnMarket: number | null;
}

export interface SoldSegment {
  label: string;
  count: number;
  avgPrice: number | null;
  medianPrice: number | null;
  avgDays: number | null;
}

export interface SoldArchive {
  total: number;
  vehicles: SoldVehicle[];
  byCategory: SoldSegment[];
  byBrand: SoldSegment[];
  byPriceBand: SoldSegment[];
  updatedAt: string;
}

const BRANDS = [
  "Rapido", "Kabe", "Adria", "Hobby", "Knaus", "Dethleffs", "Cabby", "Polar",
  "Solifer", "Hymer", "Bürstner", "Burstner", "Sunlight", "Carado", "Weinsberg",
  "Fendt", "Tabbert", "Elddis", "Chausson", "Challenger", "Benimar", "Laika",
];

function median(nums: number[]): number | null {
  if (nums.length === 0) return null;
  const s = [...nums].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : Math.round((s[m - 1] + s[m]) / 2);
}

function avg(nums: number[]): number | null {
  if (nums.length === 0) return null;
  return Math.round(nums.reduce((a, b) => a + b, 0) / nums.length);
}

function segment(label: string, rows: SoldVehicle[]): SoldSegment {
  const prices = rows.map((r) => r.price).filter((p): p is number => p != null && p > 0);
  const days = rows.map((r) => r.daysOnMarket).filter((d): d is number => d != null && d >= 0);
  return {
    label,
    count: rows.length,
    avgPrice: avg(prices),
    medianPrice: median(prices),
    avgDays: avg(days),
  };
}

export const getSoldArchive = createServerFn({ method: "GET" }).handler(
  async (): Promise<SoldArchive> => {
    const supabase = createClient(process.env.SUPABASE_URL!, process.env.SUPABASE_PUBLISHABLE_KEY!, {
      auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
    });

    const { data } = await supabase
      .from("vehicle_snapshots")
      .select("id, title, price, category, sold_at, first_seen_at")
      .not("sold_at", "is", null)
      .order("sold_at", { ascending: false })
      .limit(500);

    const rows: SoldVehicle[] = (data ?? []).map((r: any) => {
      const sold = r.sold_at ? Date.parse(r.sold_at) : NaN;
      const seen = r.first_seen_at ? Date.parse(r.first_seen_at) : NaN;
      const days =
        Number.isFinite(sold) && Number.isFinite(seen)
          ? Math.max(0, Math.round((sold - seen) / 86_400_000))
          : null;
      return {
        slug: soldSlug(r.title, String(r.id)),
        title: r.title ?? "Fordon",
        price: r.price ?? null,
        category: r.category ?? "annat",
        soldAt: (r.sold_at ?? "").slice(0, 10),
        daysOnMarket: days,
      };
    });

    const cats = Array.from(new Set(rows.map((r) => r.category)));
    const byCategory = cats
      .map((c) => segment(c, rows.filter((r) => r.category === c)))
      .filter((s) => s.count > 0)
      .sort((a, b) => b.count - a.count);

    const byBrand = BRANDS.map((b) =>
      segment(b, rows.filter((r) => r.title.toLowerCase().includes(b.toLowerCase()))),
    )
      .filter((s) => s.count > 0)
      .sort((a, b) => b.count - a.count);

    const bands: Array<[string, number, number]> = [
      ["Under 200 000 kr", 0, 199_999],
      ["200 000–399 000 kr", 200_000, 399_999],
      ["400 000–599 000 kr", 400_000, 599_999],
      ["600 000–899 000 kr", 600_000, 899_999],
      ["900 000 kr och uppåt", 900_000, Number.MAX_SAFE_INTEGER],
    ];
    const byPriceBand = bands
      .map(([label, lo, hi]) =>
        segment(label, rows.filter((r) => r.price != null && r.price >= lo && r.price <= hi)),
      )
      .filter((s) => s.count > 0);

    return {
      total: rows.length,
      vehicles: rows.slice(0, 120),
      byCategory,
      byBrand,
      byPriceBand,
      updatedAt: new Date().toISOString().slice(0, 10),
    };
  },
);
